import { WorkCalendarHelper } from "@cervval.js/calendars/src/calendars.helper";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { SECONDS_IN_ONE_DAY, SECONDS_IN_ONE_HOUR } from "@cervval.js/common/src/tools/time.tools";
import { WorkDateType, add, diff, pxToTimestamp, subtract } from "@cervval.js/common/src/tools/timestamps.tools";
import { CjsDayAgenda } from "../dayAgenda/dayAgenda";
import { DayAgendaContext } from "../dayAgenda/dayAgendaContext";
import { DayAgendaInteraction } from "../dayAgenda/dayAgendaEvents";
import { DayAgendaHeaderOptions } from "../dayAgendaHeader/dayAgendaHeader";

export interface AgendaNavigationOptions {
    /** Debounce time in MS for pan move (min time between 2 pan refresh) */
    panMoveDebounceTime: number;
    /** Debounce time in MS for zoom (min time between 2 zoom refresh) */
    zoomDebounceTime: number;
    /** Zoom scale divider must be !== 0 */
    zoomScaleDivider: number;
    /** Max zoom level (number of seconds between start and finish) */
    minZoomLevelInSeconds: number;
    /** Max zoom level (number of seconds between start and finish) */
    maxZoomLevelInSeconds: number;
    /** Array of agendaTimescale options */
    agendaHeaderOptions: Partial<DayAgendaHeaderOptions>[];
    /** Navigation buttons are enabled by default */
    disableNavigationButtons?: boolean;
    /** Calendar helper of the agenda */
    workCalendarHelper?: WorkCalendarHelper;
}

const ON_PAN_CSS_CLASS = "on-pan-event";// class added to the right panel and to navigation buttons when pan is active (changes cursor, changes opacity)

/**
 * Function that install pan move by listening to mousedown event
 */
export function installPanMove<
    ItemObject,
    ColumnObject
>(agenda: CjsDayAgenda<ItemObject, ColumnObject>, context: DayAgendaContext): void {
    let panTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    let nbPanDone = 0;
    let isPanning: boolean = false;
    let lastPanMs: number = 0;
    let initialX: number;
    let isWheelButtonPressed = false;

    const onMouseMove = (e: MouseEvent): void => {
        if (isWheelButtonPressed) {
            if (e.clientX !== initialX) {
                if (panTimeout) {
                    clearTimeout(panTimeout);
                }
                const doPan = (): void => {
                    if (isPanning) {
                        // panning is in progress...
                        if (panTimeout) {
                            clearTimeout(panTimeout);
                        }
                        // debounce and process later
                        panTimeout = setTimeout(doPan, agenda.options.panMoveDebounceTime);
                        return;
                    }
                    // record there is no "waiting" pan
                    panTimeout = undefined;
                    // record there is a pan in progress
                    isPanning = true;
                    try {
                        const panX = initialX - e.clientX;
                        if (pan(agenda, context, panX)) {
                            nbPanDone++;
                        }
                    } catch (err) {
                        getLogConsole().error(err);
                    } finally {
                        isPanning = false;
                    }
                    lastPanMs = Date.now();
                    initialX = e.clientX;
                };
                if (!isPanning && Date.now() - lastPanMs >= agenda.options.panMoveDebounceTime) {
                    // process one pan now
                    doPan();
                } else {
                    // debounce and process later
                    panTimeout = setTimeout(doPan, agenda.options.panMoveDebounceTime);
                }
            }
        }
    };

    const onMouseDown = (e: MouseEvent): void => {

        if (e.button === 1) { // We only want to perform interaction on middle click, cf https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button

            // prevent browser from handling event
            e.preventDefault();

            // init vars for mouse move
            isWheelButtonPressed = true;
            initialX = e.clientX;
            nbPanDone = 0;
            lastPanMs = Date.now() - agenda.options.panMoveDebounceTime;// -panMoveDebounceTime => force doPan on first move event

            // update cursor
            agenda.components.columnsContainer.classList.add(ON_PAN_CSS_CLASS);

            // pan on mouse move
            agenda.components.columnsContainer.addEventListener("mousemove", onMouseMove);

            // on next mouse up anywhere on document, we finish the interaction
            document.addEventListener("mouseup", (e) => {
                isWheelButtonPressed = false;

                // stop showing pan cursor
                agenda.components.columnsContainer.classList.remove(ON_PAN_CSS_CLASS);

                // remove mousemove event
                agenda.components.columnsContainer.removeEventListener("mousemove", onMouseMove);

                if (nbPanDone > 0 || isPanning || panTimeout) {
                    if (isPanning || panTimeout) {
                        // panning in progress => wait
                        const waitForPanFinish = setInterval(() => {
                            if (!isPanning && !panTimeout) {
                                clearInterval(waitForPanFinish);
                                agenda.interactionFinishEvent.emit({ event: DayAgendaInteraction.PAN, refreshCount: nbPanDone });
                            }
                        }, 10);
                    } else {
                        agenda.interactionFinishEvent.emit({ event: DayAgendaInteraction.PAN, refreshCount: nbPanDone });
                    }
                }
            }, {
                once: true
            });
        }
    };
    agenda.components.columnsContainer.addEventListener("mousedown", onMouseDown, { passive: false });
}

/** 
 * Pans (moves horizontally) the agenda displayed with the context by panX pixels
 * @returns true if pan was performed
 */
function pan<
    ItemObject,
    ColumnObject
>(agenda: CjsDayAgenda<ItemObject, ColumnObject>, context: DayAgendaContext, panX: number): boolean {
    if (context.width === 0 || panX === 0) { return false; }
    const diffSecs = diff(context.startTs, context.finishTs, "second");

    // move
    const delta = diffSecs * panX / context.width;// How many seconds will be added/subtracted to startDate/finishDate on pan move tick

    // TODO improve
    if (Math.abs(delta) > (SECONDS_IN_ONE_HOUR * 6)) {
        const offset = delta > 0 ? 1 : -1;

        context.startTs = add(context.startTs, { day: offset }).uts;
        context.finishTs = add(context.finishTs, { day: offset }).uts;
    }

    agenda.interactionRefreshEvent.emit({ event: DayAgendaInteraction.PAN });
    return true;
}

/**
 * Function that install zoom on agenda by listening to ctrl + scroll event
 */
export function installZoom<
    ItemObject,
    ColumnObject
>(agenda: CjsDayAgenda<ItemObject, ColumnObject>, context: DayAgendaContext): void {
    let refreshCount = 0;
    let interactionFinishTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    /** ctrl + wheel zooms, on agendaTimescale wheel pans */
    function wheelListener(this: HTMLElement, e: WheelEvent): void {
        if (e.ctrlKey) {
            if (agenda.options.zoomScaleDivider !== 0) {
                // prevent browser from zooming globally
                e.preventDefault();
                e.stopImmediatePropagation();

                // record instant under mouse before zoom
                const rowContainerX = agenda.getAgendaColumnsContainerX();
                const underMouseTs = pxToTimestamp(context, { px: e.x - rowContainerX, targetDateType: WorkDateType.START });

                // compute and apply zoom ratio
                const diffSecs = diff(context.startTs, context.finishTs, "second");

                if (diffSecs < agenda.options.minZoomLevelInSeconds && e.deltaY < 0) { return; }
                if (diffSecs > agenda.options.maxZoomLevelInSeconds && e.deltaY > 0) { return; }

                const zoomRatio = -Math.sign(e.deltaY) * diffSecs / agenda.options.zoomScaleDivider; // How many seconds will be added/subtracted to startDate and finishDate on Zoom
                if (zoomRatio === 0) { return; }
                const offset = zoomRatio > 0 ? -1 : 1;
                const zoomIn = offset < 0;

                const diffStartMouseTs = diff(context.startTs, underMouseTs, "day");
                const diffFinishMouseTs = diff(underMouseTs, context.finishTs, "day");

                // todo IMPROVE
                let newStartTs = context.startTs;
                let newFinishTs = context.finishTs;
                if (diffStartMouseTs > diffFinishMouseTs) {
                    if (zoomIn) {
                        newStartTs = add(newStartTs, { day: 1 }).uts;
                    } else {
                        newFinishTs = add(newFinishTs, { day: 1 }).uts;
                    }
                } else {
                    if (zoomIn) {
                        newFinishTs = subtract(newFinishTs, { day: 1 }).uts;
                    } else {
                        newStartTs = subtract(newStartTs, { day: 1 }).uts;
                    }
                }

                const newDiffSecs = diff(newStartTs, newFinishTs, "second");
                if (newDiffSecs < SECONDS_IN_ONE_DAY) { return; }

                context.startTs = newStartTs;
                context.finishTs = newFinishTs;

                agenda.interactionRefreshEvent.emit({ event: DayAgendaInteraction.ZOOM });
                refreshCount++;

                // debounce zoomFinishEvent and interactionFinishEvent
                if (interactionFinishTimeout) {
                    clearTimeout(interactionFinishTimeout);
                }
                interactionFinishTimeout = setTimeout(() => {
                    interactionFinishTimeout = undefined;
                    const eventRefreshCount = refreshCount;
                    refreshCount = 0;
                    agenda.interactionFinishEvent.emit({ event: DayAgendaInteraction.ZOOM, refreshCount: eventRefreshCount });
                }, agenda.options.zoomDebounceTime);
            }
        } else if (this === agenda.components.agendaHeader) {
            // prevent browser from handling the event
            e.preventDefault();
            e.stopImmediatePropagation();

            if (pan(agenda, context, e.deltaY)) {
                refreshCount++;
            }

            // debounce panMoveFinishEvent and interactionFinishEvent
            // we can use the same timeout variable as zoom since they are both triggered by the same callback
            if (interactionFinishTimeout) {
                clearTimeout(interactionFinishTimeout);
            }
            interactionFinishTimeout = setTimeout(() => {
                interactionFinishTimeout = undefined;
                const eventRefreshCount = refreshCount;
                refreshCount = 0;
                agenda.interactionFinishEvent.emit({ event: DayAgendaInteraction.PAN, refreshCount: eventRefreshCount });
            }, agenda.options.panMoveDebounceTime);
        }
    }
    // listen on right pane (rows container and agendaTimescale container)
    agenda.components.agendaHeader.addEventListener("wheel", wheelListener, { passive: false });
    agenda.components.columnsContainer.addEventListener("wheel", wheelListener, { passive: false });
}
